import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { stepByKey } from "./reportingFlow";
import ReportManagementShell from "./ReportManagementShell";

/**
 * Wraps a "Manage ESG Reporting" destination screen with a consistent explainer
 * banner: which numbered step this is, what the screen does, what it needs from
 * other steps first, and what consumes its output. Collapsible; the choice is
 * remembered per step for the session.
 *
 * Layout: the banner is rendered through the *exact same* wrapper chain every
 * step view uses — `.page-main > .page-main-inner > .page-section > .container` —
 * so it lines up to the same width and gutters as the form area below it.
 * FrameStyle then (a) keeps the single 220px fixed-header offset on the banner
 * wrapper only, and (b) strips the child view's duplicate top offset + tall
 * section rhythm so the form starts directly under the banner.
 */
export default function ReportingStepFrame({ stepKey, children }) {
  const navigate = useNavigate();
  const step = stepByKey(stepKey);
  const storageKey = `mesg.stepIntro.${stepKey}`;

  const [open, setOpen] = useState(() => {
    try {
      return sessionStorage.getItem(storageKey) !== "0";
    } catch {
      return true;
    }
  });

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      try {
        sessionStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        /* private mode — non-critical */
      }
      return next;
    });
  };

  if (!step) return <ReportManagementShell>{children}</ReportManagementShell>;

  return (
    <ReportManagementShell>
      <FrameStyle />
      <div className="mesg-step">
        <div className="page-main mesg-step__intro">
          <div className="page-main-inner">
            <div className="page-section">
              <div className="container">
                <div className="card border-0 shadow-sm bg-light">
                  <div className="card-body py-3">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div className="d-flex align-items-center gap-2">
                        {step.n != null && (
                          <span className="badge bg-dark rounded-pill">
                            Step {step.n}
                          </span>
                        )}
                        <span
                          className="fw-bold text-uppercase extra-small"
                          style={{ letterSpacing: "0.5px" }}
                        >
                          {step.name} &mdash; Manage ESG Reporting
                        </span>
                      </div>
                      <div className="d-flex gap-3 flex-shrink-0">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none extra-small"
                          onClick={toggle}
                        >
                          {open ? "Hide" : "What is this?"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-link p-0 text-decoration-none extra-small text-secondary"
                          onClick={() =>
                            navigate("/registered/site-admin/report-management")
                          }
                        >
                          &larr; All steps
                        </button>
                      </div>
                    </div>

                    {open && (
                      <div className="mt-2">
                        <p className="small text-dark mb-2">{step.what}</p>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <div className="extra-small fw-bold text-uppercase text-muted mb-1">
                              You need first
                            </div>
                            {step.needs && step.needs.length > 0 ? (
                              <ul className="small mb-0 ps-3">
                                {step.needs.map((n, i) => (
                                  <li key={i}>{n}</li>
                                ))}
                              </ul>
                            ) : (
                              <div className="small text-muted fst-italic">
                                Nothing — you can start here.
                              </div>
                            )}
                          </div>
                          <div className="col-md-6">
                            <div className="extra-small fw-bold text-uppercase text-muted mb-1">
                              This feeds
                            </div>
                            {step.feeds && step.feeds.length > 0 ? (
                              <ul className="small mb-0 ps-3">
                                {step.feeds.map((f, i) => (
                                  <li key={i}>{f}</li>
                                ))}
                              </ul>
                            ) : (
                              <div className="small text-muted fst-italic">
                                &mdash;
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mesg-step__body">{children}</div>
      </div>
    </ReportManagementShell>
  );
}

/**
 * Scoped layout normalisation for framed step pages (kept with the component,
 * same approach as KnowledgeAssistantPanel's PanelStyle). The banner and the
 * child view now share the identical `.page-main > .page-main-inner >
 * .page-section > .container` chain, so they align to the same width — these
 * rules only fix the vertical offsets:
 *   - banner wrapper keeps the one 220px offset that clears the fixed header;
 *   - the child view's own wrapper offset + tall section rhythm are removed so
 *     the form sits directly under the banner.
 */
function FrameStyle() {
  return (
    <style>{`
      .mesg-step { width: 100%; }

      /* banner: single fixed-header clearance, everything else tight */
      .mesg-step__intro > .page-main-inner {
        padding-top: 220px !important;
        padding-bottom: 0 !important;
      }
      .mesg-step__intro .page-section {
        margin-block: 0 !important;
        padding-block: 0 !important;
      }
      .mesg-step__intro .container {
        padding-block: 0 !important;
        margin-top: 0 !important;
      }
      .mesg-step__intro .card { margin-bottom: 0.75rem; }

      /* child view: no second header offset, no bolted-on mt-5/pt-5, no tall
         section rhythm — start the content right under the banner */
      .mesg-step__body .page-main {
        margin-top: 0 !important;
        padding-top: 0 !important;
      }
      .mesg-step__body .page-main-inner {
        padding-top: 0 !important;
      }
      .mesg-step__body .page-section {
        margin-block: 0 !important;
        padding-top: 0 !important;
      }
      .mesg-step__body .container {
        padding-top: 0 !important;
        margin-top: 0 !important;
      }

      @media (max-width: 767.98px) {
        .mesg-step__intro > .page-main-inner { padding-top: 190px !important; }
      }
    `}</style>
  );
}
